import React, { useId, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { HOME_FAQ_ITEMS, HOME_FAQ_SHORT } from '../data/homeFaq';

interface FaqItem {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

const FaqItem: React.FC<FaqItem> = ({ question, answer, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div className="border-b border-gray-700 last:border-0">
      <button
        className="w-full flex justify-between items-center py-4 text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <h3 className="text-lg font-medium text-white">{question}</h3>
        {isOpen ? (
          <ChevronUp className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
        ) : (
          <ChevronDown className="text-[#5865F2] h-5 w-5 flex-shrink-0" />
        )}
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={contentId}
            role="region"
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-gray-300 pb-4">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface FaqProps {
  showLink?: boolean;
  showAllQuestions?: boolean;
}

const Faq: React.FC<FaqProps> = ({ showLink = true, showAllQuestions = false }) => {
  const allFaqItems: FaqItem[] = HOME_FAQ_ITEMS;
  
  // Liste réduite de 6 questions pour la page d'accueil
  const shortFaqItems: FaqItem[] = HOME_FAQ_SHORT;
  
  // Choisir quelle liste afficher
  const faqItems = showAllQuestions ? allFaqItems : shortFaqItems;

  return (
    <section id="faq" className="section-spacing bg-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-[#2F3136] p-8 rounded-xl border border-gray-700 shadow-xl overflow-hidden max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="text-[#5865F2] h-8 w-8 mr-3" />
            <h2 className="text-2xl md:text-3xl font-bold text-white">Foire Aux <span className="text-[#5865F2]">Questions</span></h2>
          </div>
          <p className="text-gray-300 mb-8 text-center max-w-2xl mx-auto">
            Retrouvez les réponses à nos questions les plus fréquentes. Si vous ne trouvez pas ce que vous cherchez,
            n'hésitez pas à nous contacter directement.
          </p>
          
          <div className="bg-[#36393F] rounded-lg overflow-hidden border border-gray-700">
            <div className="p-6 space-y-0">
              {faqItems.map((item, index) => (
                <FaqItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-300 mb-4">
              {showLink ? (
                <><RouterLink to="/depannage-informatique" className="text-[#5865F2] hover:underline">D'autres questions ?</RouterLink> Contactez-nous directement :</>
              ) : (
                <>D'autres questions ? Contactez-nous directement :</>
              )}
            </p>
            <RouterLink
              to="/contact"
              className="group relative inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white transition-all duration-300 ease-in-out transform hover:scale-105 bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] rounded-lg shadow-lg hover:shadow-[#5865F2]/50"
            >
              <span className="relative z-10 flex items-center">
                Nous contacter
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-[#4752C4] via-[#5865F2] to-[#4752C4] rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </RouterLink>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Faq;